import { useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { trackProductEvent } from '../../lib/product-analytics';
import { usePersistOnboardingPreferences } from '../../hooks/usePersistOnboardingPreferences';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { WebSaveButton } from '../cv/forms/web-form-elements';
import { useOnboarding } from './OnboardingProvider';
import { OnboardingWebStep } from './web-step-chrome';
import {
  WHERE_HEARD_OPTIONS as OPTIONS,
  WHERE_HEARD_SOCIAL_PLATFORMS as SOCIAL_PLATFORMS,
  type WhereHeardOption as Option,
  type WhereHeardSocialPlatform as SocialPlatform,
} from './where-heard-options';

/**
 * Web variant of "Where did you hear about us?": single-select attribution
 * survey wired into OnboardingProvider, mirroring the newer native step:
 * skippable, social-platform chips, free-text "other"
 * detail. The choice is persisted as `{ whereHeard }` and captured via product
 * analytics; the route layout provides the progress + shell.
 *
 * PRESERVED step (owner decision 2026-10-04): the survey left the onboarding
 * flow; the same question is asked once in the app when a CV is opened from
 * My Tabbio (components/cv/WhereHeardPrompt.web). The route stays valid for
 * old deep links and still completes onboarding through finishWhereHeard.
 */
export function StepWhereHeard() {
  const { t } = useTranslation('auth');
  const { prevStep, finishWhereHeard } = useOnboarding();
  const [selected, setSelected] = useState<Option | null>(null);
  const [socialPlatform, setSocialPlatform] = useState<SocialPlatform | null>(null);
  const [otherText, setOtherText] = useState('');
  const [isAdvancing, setIsAdvancing] = useState(false);
  const { isPersistingPreferences, persistOnboardingPreferences } =
    usePersistOnboardingPreferences({ step: 'where-heard' });

  const canContinue =
    selected !== null &&
    (selected !== 'social' || socialPlatform !== null) &&
    !isPersistingPreferences &&
    !isAdvancing;

  // Persist attribution, then advance to the next flow step.
  const handleNext = async () => {
    if (!selected) return;
    trackProductEvent('onboarding_where_heard', {
      source: selected,
      placement: 'onboarding',
      ...(selected === 'social' && socialPlatform ? { platform: socialPlatform } : {}),
      ...(selected === 'other' && otherText.trim() ? { detail: otherText.trim() } : {}),
    });
    const didPersist = await persistOnboardingPreferences({
      whereHeard: selected,
    });
    if (didPersist) {
      setIsAdvancing(true);
      await finishWhereHeard('next');
      setIsAdvancing(false);
    }
  };

  const handleSkip = async () => {
    if (isAdvancing) return;
    setIsAdvancing(true);
    await finishWhereHeard('skip');
    setIsAdvancing(false);
  };

  return (
    <OnboardingWebStep
      titleBase={t('onboarding.whereHeard.title')}
      titleAccent={t('onboarding.whereHeard.titleAccent')}
      onBack={prevStep}
      onSkip={() => void handleSkip()}
    >
      <div role="radiogroup" style={listStyle}>
        {OPTIONS.map((key) => {
          const active = selected === key;
          let expansion: ReactNode = null;
          if (active && key === 'social') {
            expansion = (
              <div style={chipsRowStyle}>
                {SOCIAL_PLATFORMS.map((platform) => {
                  const chipActive = socialPlatform === platform;
                  return (
                    <button
                      type="button"
                      key={platform}
                      onClick={() => setSocialPlatform(platform)}
                      aria-pressed={chipActive}
                      style={{
                        ...chipStyle,
                        backgroundColor: chipActive ? 'var(--text-primary)' : 'var(--bg)',
                        color: chipActive ? 'var(--bg)' : 'var(--text-secondary)',
                      }}
                    >
                      {t(`onboarding.whereHeard.socialPlatforms.${platform}`)}
                    </button>
                  );
                })}
              </div>
            );
          } else if (active && key === 'other') {
            expansion = (
              <input
                type="text"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder={t('onboarding.whereHeard.otherPlaceholder')}
                dir="auto"
                style={otherInputStyle}
              />
            );
          }
          return (
            <OptionTile
              key={key}
              active={active}
              onSelect={() => setSelected(key)}
              label={t(`onboarding.whereHeard.options.${key}`)}
              expansion={expansion}
            />
          );
        })}
      </div>

      <WebSaveButton
        label={t('onboarding.next')}
        onClick={handleNext}
        loading={isPersistingPreferences || isAdvancing}
        disabled={!canContinue}
      />
    </OnboardingWebStep>
  );
}

/**
 * Selectable option tile: the plan-card recipe (borderless tinted fill +
 * trailing check_circle / radio_button_unchecked). The optional expansion
 * (social chips / "other" input) renders below the row inside the same tint so
 * interactive children never nest inside the button.
 */
function OptionTile({
  active,
  onSelect,
  label,
  expansion,
}: {
  active: boolean;
  onSelect: () => void;
  label: string;
  expansion?: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...tileStyle,
        backgroundColor: active
          ? 'rgba(0,0,0,0.06)'
          : hovered
            ? 'rgba(0,0,0,0.05)'
            : 'rgba(0,0,0,0.04)',
      }}
    >
      <button type="button" role="radio" aria-checked={active} onClick={onSelect} style={tileButtonStyle}>
        <span style={tileLabelStyle}>{label}</span>
        <MaterialSymbol
          name={active ? 'check_circle' : 'radio_button_unchecked'}
          size={22}
          style={{ color: active ? 'var(--text-primary)' : 'var(--text-tertiary)', flexShrink: 0 }}
        />
      </button>
      {expansion ? <div style={expansionStyle}>{expansion}</div> : null}
    </div>
  );
}

const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginTop: 4,
};

const tileStyle: CSSProperties = {
  borderRadius: 16,
  transition: 'background-color 150ms',
};

const tileButtonStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 18px',
  textAlign: 'start',
  color: 'var(--text-primary)',
  border: 'none',
  borderRadius: 16,
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const tileLabelStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 15,
  fontWeight: 500,
};

const expansionStyle: CSSProperties = {
  padding: '0 18px 14px',
};

const chipsRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
};

const chipStyle: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 9999,
  border: 'none',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: 'background-color 150ms, color 150ms',
};

const otherInputStyle: CSSProperties = {
  width: '100%',
  height: 44,
  borderRadius: 12,
  border: '1px solid var(--gem-outline-variant)',
  backgroundColor: 'var(--bg)',
  padding: '0 14px',
  fontSize: 14,
  fontFamily: 'inherit',
  color: 'var(--text-primary)',
  outline: 'none',
  boxSizing: 'border-box',
};
